<template>
  <TheHeaderContent
    id="headerId"
    brandTitle="City Of Dreams"
    v-bind="$attrs"
    :showProfile="showProfile"
    :showCurrency="showCurrency"
    :showMultiLang="shouldShowMultiLang"
    @update-profile="profileStore.setIsProfileModalOpen(true)"
    @show-profile="onShowProfile"
    @review-message="messagesStore.toggleIsOpen(true)"
    @review-trips="tripStore.setIsTripModalOpen(true)"
    @loyalty-account="loyaltyStore.setIsLoyaltyModalOpen(true)"
    @contact-us="supportStore.toggleIsOpen(true)"
    @show-itinerary="itinerarySummaryStore.toggle()"
    @logout="authStore.toggleLogoutModal()"
    @affiliate="validateAffiliateUserAuth"
  />

  <!-- PROFILE -->
  <ProfileConfirmationProvider />

  <!-- LOYALTY -->
  <LoyaltyFormConfirmationProvider>
    <LoyaltyModal />
  </LoyaltyFormConfirmationProvider>

  <!-- MESSAGES -->
  <ReviewMessageModal v-model="messagesStore.isOpen" />

  <!-- TRIPS -->
  <TripsModal v-model="tripStore.isTripModalOpen" />

  <!-- AUTH -->
  <AuthModal
    v-if="featureFlag.authMode === 'legacy'"
    v-model="authStore.authFormIsOpen"
  />

  <AffiliateModal v-model="affiliateStore.isOpenAffiliateModal" />
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import TheHeaderContent from './HeaderContent/TheHeaderContent.vue'
import type { Props } from './TheHeader.types'
import LoyaltyFormConfirmationProvider from '@/modules/Loyalty/components/LoyaltyFormConfirmationProvider/LoyaltyFormConfirmationProvider.vue'
import LoyaltyModal from '@/modules/Loyalty/components/LoyaltyModal/LoyaltyModal.vue'
import useLoyaltyStore from '@/modules/Loyalty/store'
import useProfileStore from '@/modules/Profile/store'
import ProfileConfirmationProvider from '@/modules/Profile/components/ProfileConfirmationProvider/ProfileConfirmationProvider.vue'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import useTripsStore from '@/modules/Trips/store'
import { useCustomerLanguageQuery } from '@/modules/Core/requests/queries/useCustomerLanguageQuery'
import useSupportStore from '@/modules/Support/store'
import useMessagesStore from '@/modules/Messages/store'
import useAuth from '@/modules/Auth/composables/useAuth'
import useAuthStore from '@/modules/Auth/store'
import featureFlag from '@/config/featureFlag'
import useAffiliateStore from '@/modules/Affiliate/store'
import AffiliateModal from '@/modules/Affiliate/components/AffiliateModal/AffiliateModal.vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<Props>(), {
  showProfile: true,
  showCurrency: true,
  showMultiLang: undefined,
})

const customerLanguageQuery = useCustomerLanguageQuery({
  enabled: props.showMultiLang,
})

const shouldShowMultiLang = computed(() => {
  const config = customerLanguageQuery.data?.value?.data || ['en']
  return props.showMultiLang ?? config.length > 1
})

// ASYNC MODAL COMPONENTS
const ReviewMessageModal = defineAsyncComponent(
  () =>
    import(
      '@/modules/Messages/components/ReviewMessageModal/ReviewMessageModal.vue'
    )
)
const AuthModal = defineAsyncComponent(
  () => import('@/modules/Auth/components/AuthModal/AuthModal.vue')
)
const TripsModal = defineAsyncComponent(
  () =>
    import('@/modules/Trips/components/ReviewTripsModal/ReviewTripsModal.vue')
)

const itinerarySummaryStore = useItinerarySummary()

// TRIP
const tripStore = useTripsStore()

// AUTH
// const authStore = useAuthStore()

// PROFILE FORMS CONFIRMATION DIALOG
const profileStore = useProfileStore()

// LOYALTY FORMS CONFIRMATION DIALOG
const loyaltyStore = useLoyaltyStore()

const authStore = useAuthStore()

const { loginWithRedirect } = useAuth()

function onShowProfile() {
  if (featureFlag.authMode === 'legacy') {
    authStore.toggleAuthForm(true)
    return
  }

  loginWithRedirect()
}

// SUPPORT DIALOG
const supportStore = useSupportStore()

// MESSAGES DIALOG
const messagesStore = useMessagesStore()

const affiliateStore = useAffiliateStore()
function validateAffiliateUserAuth() {
  if (authStore.loggedIn) {
    affiliateStore.toggleAffiliateModal()
    return
  }

  affiliateStore.setNotRegisteredAffiliateModal(true)

  if (featureFlag.authMode === 'legacy') {
    authStore.toggleAuthForm(true)
    return
  }

  loginWithRedirect()
}
</script>
